import { get } from 'lodash'

import { Services, isValidEmail } from '@sigma-frontend-libs/commons'
import { NAME_REGEXP } from '@sigmasms/commons'

import { getRUDate, globalErrorHandler, isValidPhone, leadZero, proxy, request } from '@/utils'

import render from './render'

export default {
  name: 'Form',

  mixins: [ proxy({ type: 'object' }) ],

  props: {
    ListId: String,
    isFilter: Boolean,
    isUpdate: Boolean
  },

  data() {
    return {
      show: false,
      permanent: false,
      blocked: {
        phone: false,
        email: false
      }
    }
  },

  computed: {
    canUpdateContactsAsAdmin() {
      return this.checkPermissions(`advanced.${Services.contacts}.update`)
    },

    computedDate() {
      if (this.proxy.date) {
        return getRUDate(this.proxy.date)
      }

      return this.getTranslate(`${Services.contacts}.subheaders.date`)
    },

    customFields() {
      const result = []
      for (let index = 1; index <= 20; index++) {
        result.push(`custom${leadZero(index)}`)
      }

      return result
    },

    hasPhone() {
      return Boolean(this.proxy.phone)
    },

    hasEmail() {
      return Boolean(this.proxy.email)
    },

    phoneInvalid() {
      if (!this.hasPhone || this.blocked.phone) {
        return false
      }

      return !isValidPhone(this.proxy.phone)
    },

    emailInvalid() {
      if (!this.hasEmail || this.blocked.email) {
        return false
      }

      return !isValidEmail(this.proxy.email)
    },

    firstNameInvalid() {
      return this.isNameInvalid(this.proxy.firstName)
    },

    lastNameInvalid() {
      return this.isNameInvalid(this.proxy.lastName)
    },

    middleNameInvalid() {
      return this.isNameInvalid(this.proxy.middleName)
    },

    phoneError() {
      if (this.isFilter) {
        return undefined
      }

      if (!this.hasPhone && !this.hasEmail) {
        return this.getTranslate(`${Services.contacts}.errors.phoneOrEmail`)
      }

      if (!this.phoneInvalid) {
        return undefined
      }

      return this.getTranslate(`${Services.contacts}.errors.phone`)
    },

    emailError() {
      if (this.isFilter) {
        return undefined
      }

      if (!this.hasPhone && !this.hasEmail) {
        return this.getTranslate(`${Services.contacts}.errors.phoneOrEmail`)
      }

      if (!this.emailInvalid) {
        return undefined
      }

      return this.getTranslate(`${Services.contacts}.errors.email`)
    },

    firstNameError() {
      if (this.isFilter || !this.firstNameInvalid) {
        return undefined
      }

      return this.getTranslate(`${Services.contacts}.errors.fio`)
    },

    lastNameError() {
      if (this.isFilter || !this.lastNameInvalid) {
        return undefined
      }

      return this.getTranslate(`${Services.contacts}.errors.fio`)
    },

    middleNameError() {
      if (this.isFilter || !this.middleNameInvalid) {
        return undefined
      }

      return this.getTranslate(`${Services.contacts}.errors.fio`)
    },

    validationErrors() {
      const result = []

      for (const error of Object.values(this.errors)) {
        if (error && !result.includes(error)) {
          result.push(error)
        }
      }

      return result
    },

    validationErrorInfo() {
      if (!this.validationErrors.length) {
        return undefined
      }

      return this.validationErrors.join('<br>')
    },

    phoneRequiredState() {
      if (this.isFilter || this.blocked.phone) {
        return false
      }

      return !this.hasEmail
    },

    emailRequiredState() {
      if (this.isFilter || this.blocked.email) {
        return false
      }

      return !this.hasPhone
    },

    errors() {
      return {
        phone: this.phoneError,
        email: this.emailError,
        firstName: this.firstNameError,
        lastName: this.lastNameError,
        middleName: this.middleNameError
      }
    },

    required() {
      return {
        phone: this.phoneRequiredState,
        email: this.emailRequiredState
      }
    },

    validation() {
      if (!this.hasPhone && !this.hasEmail) {
        return false
      }

      if (this.phoneInvalid) {
        return false
      }

      if (this.emailInvalid) {
        return false
      }

      if (this.firstNameInvalid || this.lastNameInvalid || this.middleNameInvalid) {
        return false
      }

      return true
    }
  },

  watch: {
    value: {
      handler() {
        this.setBlockedFields()
      },

      immediate: true
    },

    ListId(value) {
      this.proxy.ListId = value
      this.checkPermanent()
    },

    validation() {
      this.$emit('validation', this.validation)
    }
  },

  mounted() {
    this.proxy.ListId = this.ListId
    this.$emit('validation', this.validation)
    this.checkPermanent()
  },

  methods: {
    isNameInvalid(value) {
      if (!value) {
        return false
      }

      return !NAME_REGEXP.test(value)
    },

    setBlockedFields() {
      const value = this.value || {}

      this.blocked.phone = Boolean(this.isUpdate && value.phone)
      this.blocked.email = Boolean(this.isUpdate && value.email)
    },

    async checkPermanent() {
      if (this.isFilter || !this.canUpdateContactsAsAdmin) {
        return
      }

      try {
        const data = await request.find(Services.settingsSite)
        if (data) {
          const mainListId = get(data, 'globalBlacklistManager.mainListId')
          if (mainListId) {
            this.permanent = mainListId === this.proxy.ListId
          }
        }
      } catch (error) {
        globalErrorHandler(error)
      }
    }
  },

  render
}
